import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { api } from "@/api";
import { BulkBar } from "@/components/pipeline/BulkBar";
import { CreateLeadDialog } from "@/components/pipeline/CreateLeadDialog";
import { LeadPage } from "@/components/pipeline/lead/LeadPage";
import { HoverPreview } from "@/components/pipeline/lead/HoverPreview";
import { PipelineBoard } from "@/components/pipeline/PipelineBoard";
import { PipelineList } from "@/components/pipeline/PipelineList";
import { PipelineSynthesis } from "@/components/pipeline/PipelineSynthesis";
import { SyncChip } from "@/components/pipeline/SyncChip";
import { StartSessionButton } from "@/components/session/StartSessionButton";
import { PipelineToolbar } from "@/components/pipeline/PipelineToolbar";
import { cleanFilters, errText, euros } from "@/components/pipeline/utils";
import "@/pipeline.css";

const PIPELINE_KEY = "airthermo-pipeline";
const BASE = { show_archived: false, pipeline: "", pipelines: [], step_ids: [], owners: [], creators: [], date_from: "", date_to: "", tags: [], depts: [], city: "", status: "open", q: "", dormant: false, dormant_days: 14, overdue: false, today_activity: false, activity: "", remind_today: false };

export default function PipelinePage() {
  const [meta, setMeta] = useState(null);
  const [filters, setFilters] = useState(BASE);
  const [mode, setMode] = useState("board");
  const [board, setBoard] = useState(null);
  const [list, setList] = useState(null);
  const [sort, setSort] = useState("remind");
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState([]);
  const [views, setViews] = useState([]);
  const [openId, setOpenId] = useState(() => Number(new URLSearchParams(window.location.search).get("lead")) || null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [version, setVersion] = useState(0);
  const timer = useRef(null);
  const played = useRef(false);
  const parkRef = useRef(null);
  const allPipelines = useRef(new URLSearchParams(window.location.search).get("focus") === "search");

  useEffect(() => {
    api.get("/pipeline/meta").then(({ data }) => {
      // Un seul pipeline chargé à la fois (le dernier choisi, sinon le plus rempli) : la vue colonnes reste légère
      const saved = localStorage.getItem(PIPELINE_KEY);
      const busiest = [...data.pipelines].sort((a, b) => b.open_count - a.open_count)[0]?.name || "";
      const pipeline = saved !== null && (saved === "" || data.pipelines.some((p) => p.name === saved)) ? saved : busiest;
      if (!allPipelines.current) setFilters((f) => ({ ...f, pipeline }));
      setMeta(data);
    }).catch((e) => toast.error(errText(e)));
    api.get("/pipeline/views").then(({ data }) => setViews(data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    const body = cleanFilters(filters);
    try {
      if (mode === "board") {
        setBoard((await api.post("/pipeline/board", body)).data);
        setVersion((v) => v + 1);
      } else {
        setList((await api.post("/pipeline/search", { ...body, sort, offset: page * 50, limit: 50 })).data);
      }
    } catch (e) { toast.error(errText(e)); }
  }, [filters, mode, sort, page]);

  useEffect(() => {
    if (!meta) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(load, filters.q ? 300 : 0);
    return () => clearTimeout(timer.current);
  }, [load, meta, filters.q]);
  useEffect(() => { setPage(0); setSelected([]); }, [filters, mode, sort]);
  useEffect(() => {
    if (!meta) return;
    if (allPipelines.current && !filters.pipeline) return; // « Tous » imposé par la recherche du dock : non mémorisé
    allPipelines.current = false;
    localStorage.setItem(PIPELINE_KEY, filters.pipeline || "");
  }, [meta, filters.pipeline]);

  // Après un enregistrement dans la fiche : la carte est mise à jour sur place, sans recharger tout le tableau
  const patchCard = (doc) => {
    if (!doc?.lead_id) return;
    if (mode !== "board") { setList((l) => l && { ...l, rows: l.rows.map((r) => (r.lead_id === doc.lead_id ? { ...r, ...doc } : r)) }); return; }
    const keep = filters.status !== "open" || ["todo", "standby"].includes(doc.status);
    setBoard((b) => {
      const old = b?.columns.find((c) => c.cards.some((x) => x.lead_id === doc.lead_id));
      if (!old) return b;
      const card = { ...old.cards.find((x) => x.lead_id === doc.lead_id), ...doc };
      return {
        ...b,
        columns: b.columns.map((c) => {
          if (c.id === old.id && c.id === doc.step_id && keep) return { ...c, cards: c.cards.map((x) => (x.lead_id === doc.lead_id ? card : x)) };
          if (c.id === old.id) return { ...c, count: c.count - 1, cards: c.cards.filter((x) => x.lead_id !== doc.lead_id) };
          if (c.id === doc.step_id && keep) return { ...c, count: c.count + 1, cards: [card, ...c.cards] };
          return c;
        }),
      };
    });
  };

  const switchMode = (m) => {
    setMode(m);
  };

  const move = async (lead, col) => {
    setBoard((b) => ({
      ...b,
      columns: b.columns.map((c) => {
        if (c.id === lead.step_id) return { ...c, count: c.count - 1, amount: c.amount - (lead.amount || 0), cards: c.cards.filter((x) => x.lead_id !== lead.lead_id) };
        if (c.id === col.id) return { ...c, count: c.count + 1, amount: c.amount + (lead.amount || 0), cards: [{ ...lead, step_id: col.id, step: col.name, updated_at: new Date().toISOString() }, ...c.cards] };
        return c;
      }),
    }));
    try {
      await api.patch(`/pipeline/leads/${lead.lead_id}`, { step_id: col.id });
      toast.success(`« ${lead.title} » → ${col.name}`);
    } catch (e) { toast.error(errText(e)); load(); }
  };

  const runBulk = async (action, value) => {
    setBusy(true);
    try {
      const { data } = await api.post("/pipeline/bulk", { lead_ids: selected, action, value: String(value) });
      toast[data.errors.length ? "warning" : "success"](`${data.done} opportunité(s) mise(s) à jour${data.errors.length ? `, ${data.errors.length} en erreur : ${data.errors[0].error}` : ""}.`);
      setSelected([]);
      load();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  const saveView = async (name) => {
    try { const { data } = await api.post("/pipeline/views", { name, filters, mode }); setViews([...views, data]); toast.success("Vue enregistrée."); } catch (e) { toast.error(errText(e)); }
  };
  const deleteView = async (id) => { await api.delete(`/pipeline/views/${id}`); setViews(views.filter((v) => v.id !== id)); };
  const steps = useMemo(() => (meta?.pipelines.find((p) => p.name === filters.pipeline)?.steps || []), [meta, filters.pipeline]);
  const { search, key: navKey, pathname } = useLocation();
  const navigate = useNavigate();
  // La fiche s'ouvre en page complète ; le Pipeline reste monté (caché) pour un retour instantané au même endroit
  const scrollY = useRef(0);
  useEffect(() => { setOpenId(Number(new URLSearchParams(search).get("lead")) || null); }, [search, navKey]);
  const openLead = useCallback((id) => { scrollY.current = window.scrollY; navigate(`${pathname}?lead=${id}`); }, [navigate, pathname]);
  useLayoutEffect(() => { window.scrollTo(0, openId ? 0 : scrollY.current); }, [openId]);
  useEffect(() => {
    const q = new URLSearchParams(search);
    if (q.get("new")) { setCreating(true); navigate(pathname, { replace: true }); }
    if (q.get("focus") === "search") { allPipelines.current = true; setFilters((f) => ({ ...f, pipeline: "" })); setMode("list"); setTimeout(() => document.querySelector(".pl-search input")?.focus(), 300); navigate(pathname, { replace: true }); }
  }, [search, meta]); // eslint-disable-line react-hooks/exhaustive-deps
  // Retour par remplacement (et non history.back) : le téléphone Ringover intégré ajoute ses propres entrées d'historique
  // Fiche précédente / suivante : même colonne du tableau, ou même liste filtrée (ordre affiché, cartes « voir plus » comprises)
  const goLead = useCallback((id) => navigate(`${pathname}?lead=${id}`, { replace: true }), [navigate, pathname]);
  const nav = useMemo(() => {
    if (!openId) return null;
    const el = document.querySelector(`[data-testid=pipeline-page] [data-lead-id="${openId}"]`);
    const scope = el?.closest(".pl-col, [data-testid=pipeline-list-table]");
    const ids = scope ? [...scope.querySelectorAll("[data-lead-id]")].map((e) => Number(e.dataset.leadId)) : [];
    const i = ids.indexOf(openId);
    return i < 0 ? null : { prev: ids[i - 1] || null, next: ids[i + 1] || null, pos: i + 1, total: ids.length, go: goLead };
  }, [openId, goLead, board, list, version]); // eslint-disable-line react-hooks/exhaustive-deps
  const onClose = useCallback(() => navigate(pathname, { replace: true }), [navigate, pathname]);

  // Le Pipeline (souvent des centaines de cartes) n'est pas re-rendu quand on ouvre ou change de fiche : ouverture instantanée
  const parked = useMemo(() => {
    if (!meta) return null;
    const summary = mode === "board" ? board : list;
    return (
      <>
      <header className="page-heading pl-heading">
        <div>
          <p className="eyebrow">PIPELINE · SYNCHRONISÉ AVEC NOCRM</p>
          <h1>Opportunités</h1>
          <SyncChip />
          <p className="page-subtitle" data-testid="pipeline-summary">
            {summary ? `${summary.total.toLocaleString("fr-FR")} opportunités${summary.amount > 0 ? ` · ${euros(summary.amount)}` : ""}` : "…"}
            {!meta.me.is_admin && " · vos opportunités uniquement"}
          </p>
        </div>
        <StartSessionButton body={{ source: "pipeline", filters: cleanFilters(filters) }} label={`Pipeline « ${filters.pipeline || "tous"} »${filters.step_ids.length ? ` · étapes : ${steps.filter((x) => filters.step_ids.includes(x.id)).map((x) => x.name).join(", ")}` : ""}${filters.q ? ` · recherche « ${filters.q} »` : ""}`} count={Math.min(summary?.total || 0, 100)} testid="pipeline-start-session" />
      </header>
      {!meta.me.is_admin && !meta.me.nocrm_user_id && <p className="pl-warning" data-testid="pipeline-no-mapping">Votre compte n'est relié à aucun commercial noCRM : demandez à un administrateur de le relier dans Paramètres › Utilisateurs.</p>}
      <PipelineToolbar meta={meta} filters={filters} setFilters={setFilters} mode={mode} setMode={switchMode} views={views} onSaveView={saveView} onApplyView={(v) => { setFilters({ ...BASE, ...v.filters }); setMode(v.mode || "board"); }} onDeleteView={deleteView} onCreate={() => setCreating(true)} />
      <PipelineSynthesis key={JSON.stringify(filters) + mode} body={cleanFilters(filters)} />
      {mode === "board"
        ? (board ? <PipelineBoard key={version} played={played} board={board} filters={filters} onOpen={openLead} onMove={move} /> : <p className="pl-muted">Chargement…</p>)
        : (list ? <PipelineList data={list} sort={sort} setSort={setSort} page={page} setPage={setPage} selected={selected} setSelected={setSelected} dormantDays={filters.dormant_days} onOpen={openLead} /> : <p className="pl-muted">Chargement…</p>)}
      {selected.length > 0 && mode === "list" && <BulkBar count={selected.length} meta={meta} steps={steps} onRun={runBulk} onClear={() => setSelected([])} busy={busy} />}
      </>
    );
  }, [meta, filters, mode, board, list, sort, page, selected, views, version, busy, steps, openLead]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!meta) return <section className="page" data-testid="pipeline-page"><p className="pl-muted" data-testid="pipeline-loading">Chargement du pipeline…</p></section>;
  return (
    <section className="page pl-page" data-testid="pipeline-page">
      {openId && <LeadPage leadId={openId} meta={meta} onClose={onClose} onChanged={patchCard} nav={nav} />}
      <div ref={parkRef} className={openId ? "pl-parked" : undefined} aria-hidden={openId ? true : undefined}>
      <HoverPreview rootRef={parkRef} disabled={Boolean(openId)} />
      {parked}
      </div>
      <CreateLeadDialog open={creating} onOpenChange={setCreating} meta={meta} pipeline={filters.pipeline} onCreated={(doc) => { load(); openLead(doc.lead_id); }} />
    </section>
  );
}
